// React Dependencies
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

// External Dependencies
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogTitle, Divider, IconButton } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';

// Internal Dependencies
import { IsEmpty, IsString } from '../../common/checks';
import Colours from '../../styles/colours';
import MText from '../common/MText';
import MButton from '../common/MButton';
import MTextField from '../common/MTextField';
import Icons from '../common/Icons';

import ProgressiveImage from '../common/ProgressiveImage';
import ApiService from '../../services.js/apiService';
import Loading from '../global/Loading';
import PoiTags from './PoiTags';
import Convert from '../../common/convert';

import MaterialDatePicker from '../global/form/MaterialDatePicker';
import MapLocationSelect from './Poi/MapLocationSelect';
import PickPoiLocationModal from './Poi/PickPoiLocationModal';
import PoiMarkerIcon from './Poi/PoiMarkerIcon';
import PhotoDetailsModalSkeleton from './PhotoDetailsModalSkeleton';

const toCoord = (location) => {
  if (IsEmpty(location) || IsEmpty(location.lat) || IsEmpty(location.lng)) {
    return null;
  }
  return {
    lat: parseFloat(location.lat),
    lng: parseFloat(location.lng),
  };
};

const PhotoDetailsModal = (props) => {
  const photoId = props.photoId || '';

  const [photo, setPhoto] = useState({});
  const [loading, setLoading] = useState(false);

  const [takenOn, setTakenOn] = useState(null);
  const [location, setLocation] = useState(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [poiCoord, setPoiCoord] = useState([]);

  const [showPickLocationModal, setShowPickLocationModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (IsEmpty(photoId)) {
        return;
      }
      try {
        setLoading(true);
        setPhoto({});

        if (!IsEmpty(photoId)) {
          const res = await ApiService.getPhotoDetail(photoId);
          setPhoto(res);
        }
      } catch (err) {
        toast.error(IsString(err) ? err : err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [photoId]);

  // Initialise the editable fields whenever a different photo is opened.
  useEffect(() => {
    setTakenOn(!IsEmpty(photo.takenOn) ? new Date(photo.takenOn) : null);
    setLocation(toCoord({ lat: photo.lat, lng: photo.lng }));
    setDescription(photo.text || '');
    setTags((!IsEmpty(photo) && !IsEmpty(photo.photoTags)) ? photo.photoTags : []);
    setPoiCoord((!IsEmpty(photo) && !IsEmpty(photo.poiCoord)) ? toCoord(photo.poiCoord) : []);
  }, [photo]);

  const onDeletePhoto = async () => {
    try {
      setIsLoading(true);

      await ApiService.deletePageSnapstyrPhoto(props.pageId, photo.id);
      toast.success('Photo deleted');

      setShowDeleteConfirm(false);
      props.onMutationComplete();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdatePhoto = async () => {
    try {
      setIsLoading(true);

      const payload = {
        takenOn:   !IsEmpty(takenOn) ? moment(takenOn).utcOffset('+08').format('YYYY-MM-DD') : null,
        text:      description,
        lat:       (!IsEmpty(location) && !IsEmpty(location.lat)) ? location.lat : null,
        lng:       (!IsEmpty(location) && !IsEmpty(location.lng)) ? location.lng : null,
        photoTags: tags || [],
      };

      await ApiService.updatePageSnapstyrPhoto(props.pageId, photo.id, payload);

      toast.success('Photo info updated');
      setShowUpdateConfirm(false);
      props.onMutationComplete();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => props.onClose();

  return (
    <>
      <Dialog
        open={props.open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="body"
        PaperProps={{
          style: {
            borderRadius: 10,
            padding:      0,
            overflowY:    'visible',
            overflow:     'hidden',
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: Colours.TranslucentDarkBox,
          },
        }}
      >
        <div className="w-100 d-flex flex-column pb-4">
          {loading ? (
            <PhotoDetailsModalSkeleton />
          ) : (
            <>
              <div className="position-relative d-flex flex-column pb-4">
                <div className="d-flex align-items-center justify-content-end">
                  <IconButton onClick={() => setShowDeleteConfirm(true)}>
                    <DeleteOutline style={{ color: Colours.Black, fontSize: 18 }} />
                  </IconButton>

                  <MButton
                    onClick={() => handleClose()}
                    disableRipple
                    className="px-4 py-3"
                    variant="text"
                    colour={Colours.Transparent}
                    icon={() => <Icons.Cross color={Colours.Black} fontSize={20} />}
                  />
                </div>

                <Divider />

                <div className="mt-3 w-100">
                  {
                    (!IsEmpty(photo) && !IsEmpty(photo.photoUrl)) && (
                      <ProgressiveImage
                        image={photo.photoUrl}
                        width="100%"
                        height="auto"
                        draggable={false}
                      />
                    )
                  }
                </div>

                <div className="w-100 d-flex flex-column pt-2 px-4">
                  {
                    (!IsEmpty(photo) && !IsEmpty(photo.contributor)) && (
                      <div
                        className="d-flex flex-row align-items-center position-relative"
                        style={{ columnGap: 4 }}
                      >
                        <MText
                          text="©"
                          size={11}
                        />
                        {
                          (!IsEmpty(photo) && !IsEmpty(photo.pageLogo)) && (
                            <ProgressiveImage
                              image={Convert.imageResizeUrl(photo.pageLogo, 24, 24)}
                              width="1rem"
                              height="1rem"
                              style={{
                                borderRadius: 99,
                                objectFit:    'cover',
                                display:      'block',
                              }}
                            />
                          )
                        }
                        <MText
                          text={`${photo.username}`}
                          size={11}
                        />
                      </div>
                    )
                  }

                  <div className="d-flex flex-column pt-3 px-3">
                    <div className="d-flex flex-row justify-content-center align-items-center pb-2">
                      <MText
                        size={12}
                        className="mr-1"
                        text="Location where photo was taken"
                      />
                      <Icons.PhotoLocation />
                    </div>

                    <MapLocationSelect
                      className="mt-0"
                      height="20dvh"
                      displayOnly
                      GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
                      staticMarkersData={
                        !IsEmpty(poiCoord)
                          ? [{ latitude: poiCoord.lat, longitude: poiCoord.lng }]
                          : []
                      }
                      staticMarkerIcon={PoiMarkerIcon}
                      markerIcon={() => <Icons.PhotoLocation withShadow />}
                      currentCoord={location}
                      mapCenterCoord={poiCoord}
                      onClick={() => setShowPickLocationModal(true)}
                    />
                  </div>

                  <div className="d-flex flex-column justify-content-center align-items-center mt-5">
                    <MText text="Photo taken date" size={12} style={{ lineHeight: '0.9rem' }} />
                    <MaterialDatePicker
                      autoOk
                      variant="outlined"
                      inputVariant="outlined"
                      className="mt-1"
                      minDate={undefined}
                      maxDate={new Date()}
                      value={takenOn}
                      onChange={(date) => setTakenOn(date)}
                      isRootStyleEnabled
                    />
                  </div>

                  <div className="w-100 d-flex flex-column justify-content-center align-items-center mt-3 px-3">
                    <MText text="Description" size={12} />
                    <MText text="(max. 1000 characters)" colour={Colours.DarkGray} size={11} style={{ lineHeight: '0.9rem' }} />
                    <MTextField
                      className="mt-1 w-100"
                      minRows={6}
                      maxRows={8}
                      value={description}
                      placeholder=""
                      onChange={(value) => setDescription(value)}
                      maxCharacters={1000}
                      showCharactersCount
                    />
                  </div>

                  <div className="w-100 d-flex flex-column justify-content-center align-items-center mt-0 px-3">
                    <MText text="Tags" size={12} />
                    <MText
                      text="(tap 'Enter' after each Tag)"
                      className="mb-1"
                      colour={Colours.DarkGray}
                      size={11}
                      style={{ lineHeight: '0.9rem' }}
                    />
                    <PoiTags
                      userTags={tags}
                      onUserTagsChange={(newTags) => setTags(newTags)}
                      textColour={Colours.Black}
                    />
                  </div>
                </div>
              </div>
              <div className="mb-4 d-flex flex-row justify-content-center">
                <MButton
                  className="px-5"
                  text="Update"
                  textColour={Colours.White}
                  variant="contained"
                  colour={Colours.Primary}
                  onClick={() => setShowUpdateConfirm(true)}
                  disabled={isLoading}
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      <PickPoiLocationModal
        GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
        open={showPickLocationModal}
        markerIcon={() => <Icons.PhotoLocation withShadow size="20px" />}
        existingMarker={location}
        mapCenterCoord={poiCoord}
        staticMarkerIcon={PoiMarkerIcon}
        staticMarkersData={
          !IsEmpty(poiCoord)
            ? [{ latitude: poiCoord.lat, longitude: poiCoord.lng }]
            : []
        }
        onClose={() => setShowPickLocationModal(false)}
        onLocationChange={(newLocation) => {
          setLocation(!IsEmpty(newLocation) ? { lat: newLocation.lat, lng: newLocation.lng } : null);
        }}
      />

      {/* Confirmation Modal when deleting */}
      <Dialog
        open={showDeleteConfirm}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>
          <div className="w-100 d-flex flex-column">
            <MText
              text="Confirmation"
              size={15}
            />
            <Divider className="my-2" />
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="d-flex flex-column pb-3">
            <MText
              text="Delete photo? Action is irreversible"
              semibold
              size={13}
            />
          </div>

          <Divider className="my-3" />

          <div className="pb-3 d-flex flex-row justify-content-between">
            <MButton
              text="Cancel"
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
            />
            <MButton
              text="Delete"
              colour={Colours.Red}
              textColour={Colours.White}
              onClick={onDeletePhoto}
              disabled={isLoading}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal updating */}
      <Dialog
        open={showUpdateConfirm}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowUpdateConfirm(false)}
      >
        <DialogTitle>
          <div className="w-100 d-flex flex-column">
            <MText
              text="Confirmation"
              size={15}
            />
            <Divider className="my-2" />
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="d-flex flex-column pb-3">
            <MText
              text="Update photo? Action is irreversible"
              semibold
              size={13}
            />
          </div>

          <Divider className="my-3" />

          <div className="pb-3 d-flex flex-row justify-content-between">
            <MButton
              text="Cancel"
              variant="outlined"
              onClick={() => setShowUpdateConfirm(false)}
            />
            <MButton
              text="Update"
              colour={Colours.Primary}
              textColour={Colours.White}
              onClick={onUpdatePhoto}
              disabled={isLoading}
            />
          </div>
        </DialogContent>
      </Dialog>
      {
        isLoading && ReactDOM.createPortal(
          <Loading zIndex={9999} />,
          document.body,
        )
      }
    </>
  );
};

PhotoDetailsModal.defaultProps = {
  open:               false,
  onClose:            () => { },
  poiCoord:           null,
  onMutationComplete: () => {},
  photoId:            0,
};

PhotoDetailsModal.propTypes = {
  open:     PropTypes.bool,
  onClose:  PropTypes.func,
  pageId:   PropTypes.number.isRequired,
  poiCoord: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  onMutationComplete: PropTypes.func,
  photoId:            PropTypes.number,
};

export default PhotoDetailsModal;
